import { RowChip } from 'components/GridItemList';
import { ClassifierData } from 'types/classifiers';
import { CompanyBankAccountsGroupedDto } from 'types/companies';
import {
    ContactDataCreateOrUpdateCompanyDto,
    ContactDataCreateOrUpdatePersonDto,
    ContactDto,
    ContactForm,
    EinvoiceOperatorType,
    PartyBlockDto,
    PartyType,
    RelationshipTypeCode,
} from 'types/contacts';
import { CustomFieldDto } from 'types/customFields';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { blocksToForm } from './blocking/BlockingPolicyReadonlyShared';
import { mapToCompanyDto, mapToPersonDto } from './ContactPageForm';
import {
    getAdministrators,
    getBusinessContacts,
    getEmployedBy,
    getEmployees,
    getManagers,
    getPartners,
    getPaymentNotificationReceivers,
    getPrimaryContact,
    getPrimaryContactForCompanies,
    getSecondaryRecipientContacts,
} from './contactRelationUtils';

/** The classifiers the contact form reads. */
export const CONTACT_FORM_CLASSIFIERS = [
    'RELATIONSHIP_TYPE',
    'PARTY_STATUS',
    'PERSON_TITLE',
    'LANGUAGE',
    'BUSINESS_AREA_TYPE',
    'COUNTRY',
    'SEGMENT_TYPE',
    'SERVICE_LEVEL_TYPE',
    'KEYWORD_TYPE',
    'COMPLIANCE_VERIFICATION_STATUS_TYPE',
    'RISK_PROFILE_TYPE',
    'MAILING_PREFERENCE_TYPE',
    'MARKETING_AREA_TYPE',
    'AREA_OF_RESPONSIBILITY_TYPE',
    'COOPERATION_AREA_TYPE',
    'RISK_FACTOR_TYPE_CODE',
];

const EINVOICE_OPERATOR_TYPE_LABELS: Record<string, string> = {
    FINVOICE: t('einvoiceOperatorType.FINVOICE'),
    PEPPOL: t('einvoiceOperatorType.PEPPOL'),
};

/** The classifier's options whose codes are listed, in the classifier's order: a multi-select's value. */
const selectedOptions = (classifier: ClassifierData | undefined, codes: string[]) =>
    getOptionsFromClassifier(classifier).filter(
        (val) => typeof val.value === 'string' && codes.includes(val.value)
    ) as { label: string; value: string }[];

/** {@link selectedOptions} as the chips of the new design's read row: what the multi-select holds, in its order. */
export const classifierChips = (classifier: ClassifierData | undefined, codes: string[] = []): RowChip[] =>
    selectedOptions(classifier, codes).map(({ value, label }) => ({ key: value, label }));

// Not set is an answer of its own: a contact without one leaves a new case's or a moved invoice's setting as is.
export const SPEC_INCLUDED_OPTIONS = [
    { value: true, label: t('yes') },
    { value: false, label: t('no') },
];

export const specIncludedOption = (value: boolean | null) =>
    SPEC_INCLUDED_OPTIONS.find((option) => option.value === value) ?? null;

const selectedOption = (classifier: ClassifierData | undefined, code: string | null | undefined) =>
    getOptionsFromClassifier<string, string>(classifier).find((val) => val.value === code) ?? null;

const preferredBankAccount = (account: { id: number } | null, bankAccountsGrouped: CompanyBankAccountsGroupedDto[]) => {
    if (!account) {
        return null;
    }
    for (const company of bankAccountsGrouped) {
        const match = company.bankAccounts.find(({ id }) => id === account.id);
        if (match) {
            return { value: match.id, label: match.iban, group: company.companyName };
        }
    }
    return null;
};

/** The edit form's values for a contact, as the contact edit page loads them. */
export const contactDtoToForm = (
    contact: ContactDto,
    {
        classifiers,
        bankAccountsGrouped,
        customFieldValues,
    }: {
        classifiers: Record<string, ClassifierData>;
        bankAccountsGrouped: CompanyBankAccountsGroupedDto[];
        /** `useCustomFields`' `initialCustomFieldsObject` for the contact. */
        customFieldValues: Record<string, unknown>;
    }
): ContactForm => ({
    partyType: contact.partyType,
    relationshipType: contact.relationshipTypeCode ?? RelationshipTypeCode.NON_CLIENT,
    name: contact.name,
    officialLocalName: contact.officialLocalName ?? '',
    position: contact.position ?? '',
    mainEmail: contact.mainEmail ?? '',
    mainPhone: contact.mainPhone ?? '',
    mainMobile: contact.mainMobile ?? '',
    statusCode: contact.statusCode,
    clientReference: contact.clientReference ?? '',
    preferredLanguage: selectedOption(classifiers['LANGUAGE'], contact.preferredLanguageCode),
    primaryContact: getPrimaryContact(contact.relations),
    primaryContactForCompanies: getPrimaryContactForCompanies(contact.relatesTo),
    billingNotes: contact.billingNotes ?? '',
    invoicingInfo: contact.invoicingInfo ?? '',
    debtNotes: contact.debtNotes ?? '',
    debtExplanation: contact.debtExplanation ?? '',
    debtHistory: contact.debtHistory ?? '',
    notes: contact.notes ?? '',
    coiNotes: contact.coiNotes ?? '',
    businessAreas: selectedOptions(
        classifiers['BUSINESS_AREA_TYPE'],
        contact.businessAreas?.map((val) => val.businessAreaCode) ?? []
    ),
    relatedKeywords: selectedOptions(
        classifiers['KEYWORD_TYPE'],
        (contact.relatedKeywords || []).map((val) => val.keywordTypeCode)
    ).map((el) => ({
        ...el,
        notes: contact.relatedKeywords?.find((keyword) => keyword.keywordTypeCode === el.value)?.notes,
    })),
    managers: getManagers(contact.managers),
    partners: getPartners(contact.partners),
    administrators: getAdministrators(contact.administrators),
    businessContacts: getBusinessContacts(contact.businessContacts),
    billingAddress: contact.billingAddress,
    billingLocalAddress: contact.billingLocalAddress,
    personalCode: contact.personalCode ?? '',
    personTitle: selectedOption(classifiers['PERSON_TITLE'], contact.personTitleCode),
    personManualTitle: contact?.personTitleText ?? '',
    linkedinUrl: contact.linkedinUrl ?? '',
    homeAddress: contact.homeAddress,
    homeLocalAddress: contact.homeLocalAddress,
    firstName: contact.firstName,
    lastName: contact.lastName,
    localFirstName: contact.localFirstName ?? '',
    localLastName: contact.localLastName ?? '',
    officialName: contact.officialName ?? '',
    regCode: contact.regCode ?? '',
    // A company with no VAT number is treated as not VAT liable so existing
    // records stay editable without retroactively demanding a VAT number. A draft may hold a VAT
    // country without its number, and stays liable so that the country is not dropped on its next save.
    notVatLiable: (!contact.vatRegNum && !contact.vatCountryCode) || !contact.isVatLiable,
    vatRegNum: contact.vatRegNum ?? '',
    vatCountryCode: selectedOption(classifiers['COUNTRY'], contact.vatCountryCode),
    website: contact.website ?? '',
    einvoiceAddress: contact.einvoiceAddress ?? '',
    einvoiceOperatorCode: contact.einvoiceOperatorCode ?? '',
    einvoiceOperatorType: contact.einvoiceOperatorType
        ? {
              value: contact.einvoiceOperatorType,
              label: EINVOICE_OPERATOR_TYPE_LABELS[contact.einvoiceOperatorType] ?? contact.einvoiceOperatorType,
          }
        : null,
    employees: getEmployees(contact.relations),
    employedBy: getEmployedBy(contact.relatesTo),
    registrationAddress: contact.registrationAddress,
    registrationLocalAddress: contact.registrationLocalAddress,
    visitingAddress: contact.visitingAddress,
    correspondenceAddress: contact.correspondenceAddress,
    segmentType: selectedOption(classifiers['SEGMENT_TYPE'], contact.segmentTypeCode),
    serviceLevelType: selectedOption(classifiers['SERVICE_LEVEL_TYPE'], contact.serviceLevelTypeCode),
    payerParty: contact.payerParty,
    useJointInvoicing: contact.useJointInvoicing ?? false,
    issueInvoicesWithCurrentDate: contact.issueInvoicesWithCurrentDate ?? false,
    isInternalClient: contact.isInternalClient ?? false,
    primaryRecipientParty: contact.primaryRecipientParty,
    primaryRecipientAdditionalEmails: contact.primaryRecipientAdditionalEmails ?? '',
    secondaryRecipientParties: getSecondaryRecipientContacts(contact.relations).map(({ id, name }) => ({
        id,
        name,
    })),
    secondaryRecipientAdditionalEmails: contact.secondaryRecipientAdditionalEmails ?? '',
    invoiceArchiveEmail: contact.invoiceArchiveEmail ?? '',
    invoiceTemplate: contact.invoiceTemplateId ? { id: contact.invoiceTemplateId } : null,
    invoiceSpecTemplate: contact.invoiceSpecTemplateId ? { id: contact.invoiceSpecTemplateId } : null,
    isSpecIncludedInInvoice: specIncludedOption(contact.isSpecIncludedInInvoice ?? null),
    invoiceCustomization: contact.invoiceCustomization ?? null,
    paymentNotificationReceivers: getPaymentNotificationReceivers(contact.paymentNotificationReceivers ?? null),
    dmsLink: contact?.dmsLink || null,
    isPoliticallyExposedPerson: contact.isPoliticallyExposedPerson ?? false,
    identityDocumentNumber: contact.identityDocumentNumber ?? null,
    identityDocumentDateOfIssue: contact.identityDocumentDateOfIssue ?? null,
    identityDocumentDateOfExpiry: contact.identityDocumentDateOfExpiry ?? null,
    identityDocumentIssuingCountryCode: selectedOption(
        classifiers['COUNTRY'],
        contact.identityDocumentIssuingCountryCode
    ),
    identityDocumentIssuingAuthority: contact.identityDocumentIssuingAuthority ?? null,
    invoicingDeadlineDay: contact.invoicingDeadlineDay
        ? { id: contact.invoicingDeadlineDay, name: contact.invoicingDeadlineDay.toString() }
        : null,
    invoicingPaymentDays: contact.invoicingPaymentDays ?? null,
    preferredBankAccount: preferredBankAccount(contact.preferredBankAccount, bankAccountsGrouped),
    externalBillingInfo: contact.externalBillingInfo ?? '',
    isPersonaNonGrata: contact.isPersonaNonGrata,
    riskProfileTypeCode: selectedOption(classifiers['RISK_PROFILE_TYPE'], contact.riskProfileTypeCode),
    riskProfileNotes: contact.riskProfileNotes ?? '',
    complianceVerificationStatusTypeCode: selectedOption(
        classifiers['COMPLIANCE_VERIFICATION_STATUS_TYPE'],
        contact.complianceVerificationStatusTypeCode
    ),
    complianceVerificationNotes: contact.complianceVerificationNotes ?? '',
    preferredMailingLanguageTypeCode: selectedOption(classifiers['LANGUAGE'], contact.preferredMailingLanguageTypeCode),
    countries: selectedOptions(classifiers['COUNTRY'], contact.countries?.map((val) => val.countryTypeCode) ?? []),
    mailingPreferences: selectedOptions(
        classifiers['MAILING_PREFERENCE_TYPE'],
        contact.mailingPreferences?.map((val) => val.mailingPreferenceTypeCode) ?? []
    ),
    marketingAreaTypes: selectedOptions(
        classifiers['MARKETING_AREA_TYPE'],
        contact.marketingAreaTypes?.map((val) => val.marketingAreaTypeCode) ?? []
    ),
    areaOfResponsibilityTypes: selectedOptions(
        classifiers['AREA_OF_RESPONSIBILITY_TYPE'],
        contact.areaOfResponsibilityTypes?.map((val) => val.areaOfResponsibilityTypeCode) ?? []
    ),
    cooperationAreaTypes: selectedOptions(
        classifiers['COOPERATION_AREA_TYPE'],
        contact.cooperationAreaTypes?.map((val) => val.cooperationAreaTypeCode) ?? []
    ),
    isServiceAgreementNotNeeded: contact.isServiceAgreementNotNeeded ?? false,
    isUboNotNeeded: contact.isUboNotNeeded ?? false,
    riskFactors: getOptionsFromClassifier(classifiers['RISK_FACTOR_TYPE_CODE']).filter(
        (val) =>
            typeof val.value === 'string' &&
            val.parameters?.['isAutomatic'] !== true &&
            contact?.riskFactors?.map((rf) => rf.riskFactorTypeCode.valueOf()).includes(val.value)
    ) as { label: string; value: string }[],
    changeReasons: null,
    blocking: blocksToForm(contact.blocks),
    partyGroups: contact.partyGroups ?? [],
    ...customFieldValues,
});

/** The create/update body for the contact form's values, as the contact form submits it. */
export const contactFormToRequest = (
    form: ContactForm,
    {
        partyType,
        mapCustomFields,
        existingBlocks,
        isEinvoicingEnabled,
        initialForm,
    }: {
        partyType: PartyType;
        /** `useCustomFields`' `mapCustomFieldsToDto` for the contact's type. */
        mapCustomFields: (formData: Record<string, unknown>) => CustomFieldDto[];
        existingBlocks: PartyBlockDto[];
        isEinvoicingEnabled: boolean | undefined;
        /** The loaded form, on an update only. */
        initialForm?: ContactForm;
    }
): ContactDataCreateOrUpdatePersonDto | ContactDataCreateOrUpdateCompanyDto => {
    const customFields = mapCustomFields(form as unknown as Record<string, unknown>);
    const data =
        partyType === PartyType.PERSON
            ? mapToPersonDto(form, customFields, existingBlocks)
            : mapToCompanyDto(form, customFields, existingBlocks);

    if (isEinvoicingEnabled || partyType !== PartyType.COMPANY || !initialForm) {
        return data;
    }
    // Without Netvisor or Maventa the e-invoicing fields are not on the form, so an update keeps the stored ones.
    return {
        ...data,
        einvoiceAddress: initialForm.einvoiceAddress || null,
        einvoiceOperatorCode: initialForm.einvoiceOperatorCode || null,
        einvoiceOperatorType: (initialForm.einvoiceOperatorType?.value ?? null) as EinvoiceOperatorType | null,
    };
};

/**
 * The form fields each contact section edits, by the section's `?edit=` id: the fields its component in
 * `form-sections/` renders and those its handlers set alongside them. Custom fields are a section of their own.
 */
export const CONTACT_SECTION_FIELDS = {
    'choose-type': ['partyType'],
    about: ['relationshipType', 'partners', 'managers', 'administrators'],
    person: [
        'firstName',
        'lastName',
        'name',
        'localFirstName',
        'localLastName',
        'personTitle',
        'personManualTitle',
        'position',
        'relatedKeywords',
        'areaOfResponsibilityTypes',
        'personalCode',
        'linkedinUrl',
        'homeAddress',
        'homeLocalAddress',
        'businessAreas',
        'businessContacts',
        'employedBy',
        'segmentType',
        'serviceLevelType',
    ],
    company: [
        'officialName',
        'name',
        'officialLocalName',
        'regCode',
        'website',
        'businessAreas',
        'registrationAddress',
        'registrationLocalAddress',
        'segmentType',
        'serviceLevelType',
    ],
    identity: [
        'identityDocumentNumber',
        'identityDocumentDateOfIssue',
        'identityDocumentDateOfExpiry',
        'identityDocumentIssuingCountryCode',
        'identityDocumentIssuingAuthority',
    ],
    'contact-details': [
        'mainPhone',
        'mainMobile',
        'mainEmail',
        'correspondenceAddress',
        'visitingAddress',
        'employees',
        'primaryContact',
        'partyGroups',
    ],
    'billing-vat': [
        'isInternalClient',
        'clientReference',
        'notVatLiable',
        'vatRegNum',
        'vatCountryCode',
        'preferredBankAccount',
        'invoicingPaymentDays',
        'payerParty',
    ],
    'billing-address': ['billingAddress', 'billingLocalAddress'],
    'billing-delivery': [
        'preferredLanguage',
        'einvoiceOperatorType',
        'einvoiceAddress',
        'einvoiceOperatorCode',
        'invoiceArchiveEmail',
        'invoiceTemplate',
        'invoiceSpecTemplate',
        'isSpecIncludedInInvoice',
        'invoiceCustomization',
    ],
    'billing-recipients': [
        'primaryRecipientParty',
        'primaryRecipientAdditionalEmails',
        'secondaryRecipientParties',
        'secondaryRecipientAdditionalEmails',
        'paymentNotificationReceivers',
    ],
    'billing-rules': [
        'billingNotes',
        'invoicingInfo',
        'useJointInvoicing',
        'issueInvoicesWithCurrentDate',
        'invoicingDeadlineDay',
        'externalBillingInfo',
    ],
    marketing: ['preferredMailingLanguageTypeCode', 'mailingPreferences', 'marketingAreaTypes', 'countries'],
    other: ['isPoliticallyExposedPerson', 'dmsLink', 'notes', 'coiNotes', 'relatedKeywords', 'cooperationAreaTypes'],
    // Each axis edits only its own controls, so the rest of `blocking` goes back as loaded.
    'blocking-cases': ['blocking'],
    'blocking-timecards': ['blocking'],
    'blocking-aml-timecards': ['blocking'],
    compliance: [
        'complianceVerificationStatusTypeCode',
        'complianceVerificationNotes',
        'riskProfileTypeCode',
        'riskProfileNotes',
        'riskFactors',
        'isServiceAgreementNotNeeded',
        'isUboNotNeeded',
        'isPersonaNonGrata',
    ],
    'custom-fields': [],
} satisfies Record<string, (keyof ContactForm)[]>;

export type ContactSectionId = keyof typeof CONTACT_SECTION_FIELDS;

/** {@link CONTACT_SECTION_FIELDS} for a party type: a person's keywords are edited under Person details, not Other. */
export const contactSectionFields = (partyType: PartyType): Record<ContactSectionId, readonly string[]> =>
    partyType === PartyType.PERSON
        ? {
              ...CONTACT_SECTION_FIELDS,
              other: CONTACT_SECTION_FIELDS.other.filter((field) => field !== 'relatedKeywords'),
          }
        : CONTACT_SECTION_FIELDS;

export type BillingSectionId = Extract<ContactSectionId, `billing-${string}`>;
